import { Loader2 } from "lucide-react";
import type { MicroAppDataMode, MicroAppSourceModeOption } from "@/types";
import { DATA_MODE_COPY, DATA_MODE_ORDER } from "./dataModes";

interface DataSourceModeChooserProps {
  sources: MicroAppSourceModeOption[];
  /** Currently chosen mode per provider. */
  modes: Record<string, MicroAppDataMode>;
  onModeChange: (provider: string, mode: MicroAppDataMode) => void;
  /** True while the bindings preview is still loading. */
  loading: boolean;
}

/**
 * Per-source data-mode chooser for the publish dialog — lets the author decide
 * *whose access* serves each live data source before the app is shared.
 */
export function DataSourceModeChooser({ sources, modes, onModeChange, loading }: Readonly<DataSourceModeChooserProps>) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-content-tertiary py-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking data sources…
      </div>
    );
  }
  if (sources.length === 0) {
    return <p className="text-sm text-content-tertiary">This is a static dashboard — no live data sources.</p>;
  }
  return (
    <div className="space-y-4">
      <p className="text-sm font-medium text-content-secondary">Data sources</p>
      {sources.map((s) => (
        <fieldset key={s.provider} className="rounded-lg border border-border-primary p-3">
          <legend className="px-1 text-sm font-medium text-content-primary">{s.label}</legend>
          <div className="space-y-2 pt-1">
            {DATA_MODE_ORDER.filter((m) => s.supportedModes.includes(m)).map((m) => (
              <label
                key={m}
                htmlFor={`mode-${s.provider}-${m}`}
                className="flex items-start gap-2.5 cursor-pointer rounded-md p-1.5 hover:bg-interactive-hover"
              >
                <input
                  id={`mode-${s.provider}-${m}`}
                  type="radio"
                  name={`mode-${s.provider}`}
                  aria-label={`${s.label}: ${DATA_MODE_COPY[m].label}`}
                  className="mt-1 accent-brand-primary"
                  checked={modes[s.provider] === m}
                  onChange={() => onModeChange(s.provider, m)}
                />
                <span>
                  <span className="block text-sm text-content-primary">{DATA_MODE_COPY[m].label}</span>
                  <span className="block text-xs text-content-tertiary">{DATA_MODE_COPY[m].consequence(s.label)}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      ))}
    </div>
  );
}
